import { SUPABASE_URL } from "../utils/supabase/url";
import { supabase, getAuthHeaders } from "../utils/supabase/client";
import { readError } from "./_http";
import type { SalesSummary } from "../sales/salesSummaryPolicy";
const base = `${SUPABASE_URL}/functions/v1/workspace-sales-summary`;
async function request(path: string, init: RequestInit = {}) {
  const response = await fetch(`${base}${path}`, {
    ...init,
    cache: "no-store",
    headers: {
      ...(await getAuthHeaders()),
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json();
}
export const FINANCE_CHANGED_EVENT = 'finance:changed';
export const SalesSummaryService = {
  subscribe(workspaceId: string, onChange: () => void, includeInvoices = true): () => void {
    // Treat database events only as invalidations. The summary API rechecks
    // current membership before returning any financial figures.
    let pending: ReturnType<typeof setTimeout> | undefined;
    const invalidate = () => {
      clearTimeout(pending);
      pending = setTimeout(onChange, 150);
    };
    const channel = supabase.channel(`home-finance:${workspaceId}:${crypto.randomUUID()}`);
    if (includeInvoices) channel.on('postgres_changes', { event: '*', schema: 'public', table: 'invoices', filter: `workspace_id=eq.${workspaceId}` }, invalidate);
    channel.on('postgres_changes', { event: '*', schema: 'public', table: 'workspace_memberships', filter: `workspace_id=eq.${workspaceId}` }, invalidate)
      .subscribe(status => { if (status === 'SUBSCRIBED') invalidate(); });
    let midnight: ReturnType<typeof setTimeout>;
    const scheduleDayChange = () => {
      const now = Date.now();
      const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
      // Find the next London date boundary, including the DST transition days.
      let low = now, high = now + 26 * 60 * 60 * 1000;
      while (high - low > 1000) {
        const middle = Math.floor((low + high) / 2);
        if (new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(middle) === day) low = middle;
        else high = middle;
      }
      midnight = setTimeout(() => { invalidate(); scheduleDayChange(); }, high - now);
    };
    scheduleDayChange();
    window.addEventListener(FINANCE_CHANGED_EVENT, invalidate);
    // Catch changes made while disconnected or in another tab, without polling.
    window.addEventListener('focus', invalidate);
    return () => {
      clearTimeout(pending);
      clearTimeout(midnight);
      void supabase.removeChannel(channel);
      window.removeEventListener(FINANCE_CHANGED_EVENT, invalidate);
      window.removeEventListener('focus', invalidate);
    };
  },
  get: (
    workspaceId: string,
  ): Promise<{ allowed: false } | { allowed: true; summary: SalesSummary }> =>
    request(`/workspaces/${encodeURIComponent(workspaceId)}/sales-summary`),
  permission: (
    workspaceId: string,
    userId: string,
  ): Promise<{ granted: boolean }> =>
    request(
      `/workspaces/${encodeURIComponent(workspaceId)}/members/${
        encodeURIComponent(userId)
      }/sales-summary-permission`,
    ),
  setPermission: async (
    workspaceId: string,
    userId: string,
    granted: boolean,
  ): Promise<{ granted: boolean }> => {
    const result = await request(
      `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(userId)}/sales-summary-permission`,
      { method: "PUT", body: JSON.stringify({ granted }) },
    );
    window.dispatchEvent(new Event(FINANCE_CHANGED_EVENT));
    return result;
  },
};
export const SALES_INVOICE_FILTER_KEY = "sales:pending-invoice-filter";
export function pendingSalesInvoiceFilter(): string | null {
  try {
    return sessionStorage.getItem(SALES_INVOICE_FILTER_KEY);
  } catch {
    return null;
  }
}
export function openSalesInvoices(filter: "unpaid" | "overdue" | "draft") {
  try {
    sessionStorage.setItem(SALES_INVOICE_FILTER_KEY, filter);
  } catch { /* Navigation still works. */ }
  window.dispatchEvent(
    new CustomEvent("app:navigate", { detail: { view: "sales" } }),
  );
}
